import { type PathParams } from "@constellation-academy/epic-ui";
import { Accordion, AccordionTab } from "primereact/accordion";
import { BreadCrumb } from "primereact/breadcrumb";
import { useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { useParams } from "react-router-dom";
import { BaseScreen } from "@components/base-screen";
import { useTranslation } from "@i18n/hooks";
import { type discountActionEditScreen_Query } from "@relay/discountActionEditScreen_Query.graphql";
import { QUERY } from "./discount-action-edit.graphql";
import { DiscountActionForm } from "./parts/discount-action-form";
import { DiscountCodeCodesTableHeader } from "./parts/discount-code-codes-table-header/discount-code-codes-table-header.component";
import { DiscountCodeTable } from "./parts/discount-code-table/discount-code-table.component";
import { DiscountCodeTableContextProvider } from "./parts/discount-code-table/discount-code-table.context";
import { NoAccess } from "../../components/no-access";
import { useHasPermissions } from "../../hooks/use-has-permissions";
import { Paths } from "../../routes/paths";
import { ArchivedDiscountActionsPath, type ActiveDiscountActionsPath } from "../discount-actions";

export const DiscountActionEditScreen = ({ isArchivePage }: { isArchivePage: boolean }) => {
	const { t } = useTranslation("common");
	const [activeIndex, setActiveIndex] = useState(0);
	const { discountActionId } =
		useParams<
			PathParams<typeof ActiveDiscountActionsPath | typeof ArchivedDiscountActionsPath>
		>();

	const query = useLazyLoadQuery<discountActionEditScreen_Query>(
		QUERY,
		{
			id: discountActionId!,
		},
		{ fetchPolicy: "store-and-network" },
	);
	const canRead = useHasPermissions(["UserInAccountPermission_DiscountAdmin_Read"]);
	const canModify = useHasPermissions(["UserInAccountPermission_DiscountAdmin_Modify"]);

	const items: Array<{ label?: string; url: string }> = [
		{
			label: t("discounts.allActions"),
			url: isArchivePage
				? Paths.archivedDiscountActions.path
				: Paths.activeDiscountActions.path,
		},
	];

	if (canModify)
		items.push({
			label: query.node?.title,
			url: isArchivePage
				? Paths.archivedDiscountActions.withId(discountActionId!).edit.path
				: Paths.activeDiscountActions.withId(discountActionId!).edit.path,
		});

	if (!canRead) return <NoAccess />;

	return (
		query.node && (
			<BaseScreen
				title={`${
					isArchivePage ? t("discounts.reactivateAction") : t("discounts.editAction")
				}: ${query.node.title}`}
			>
				<BreadCrumb className="mb-5" model={items} />
				<Accordion
					activeIndex={activeIndex}
					onTabChange={(e) => {
						setActiveIndex(e.index as number);
					}}
				>
					{canModify && (
						<AccordionTab header={t("discounts.discountActionTab")} tabIndex={0}>
							<DiscountActionForm discountActionFragmentRef={query.node} />
						</AccordionTab>
					)}
					<AccordionTab header={t("discounts.codesTab")} tabIndex={1}>
						<DiscountCodeTableContextProvider>
							<DiscountCodeCodesTableHeader
								discountActionFragmentRef={query.node}
								canModifyDiscountCodes={canModify && !isArchivePage}
							/>
							<DiscountCodeTable
								canModifyDiscountCodes={canModify && !isArchivePage}
								queryFragmentRef={query}
							/>
						</DiscountCodeTableContextProvider>
					</AccordionTab>
				</Accordion>
			</BaseScreen>
		)
	);
};
